import { useEffect, useRef, useState } from 'react';
import {
  Badge,
  Box,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  TextField,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { StoryReader } from '@/components/storytime/StoryReader';
import { StoryHistoryList } from '@/components/storytime/StoryHistoryList';
import { StoryTimeTour } from '@/components/onboarding/StoryTimeTour';
import axios from 'axios';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import type {
  StoryTimePreferences,
  StoryTimeGenerateResponse,
  StoryTimeSessionHistoryItem,
} from '@/types/storytime.types';
import { STORYTIME_LENGTH_BANDS } from '@/types/storytime.types';
import { getStorySessions } from '@/services/storytime.service';
import { useGenerateStory } from '@/hooks/useGenerateStory';
import { useLexicalLanguages } from '@/hooks/useLexical';
import { useStoryTimeSessions, useDeleteStorySession, storyTimeKeys } from '@/hooks/useStoryTimeSessions';
import { useStoryTimeTour } from '@/hooks/useStoryTimeTour';

const SESSION_LIMIT = 20;

export const StoryTimePage = () => {
  const { t } = useTranslation('common');
  const generateStory = useGenerateStory();
  const deleteSession = useDeleteStorySession();
  const { data: availableLanguages = [] } = useLexicalLanguages();
  const queryClient = useQueryClient();
  const { showTour, completeTour, skipTour, resetTour, wasSkipped } = useStoryTimeTour();
  const formRef = useRef<HTMLDivElement>(null);
  const [storyResult, setStoryResult] = useState<{
    response: StoryTimeGenerateResponse;
    topic: string;
    storyLanguage?: string;
  } | null>(null);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);

  // History pagination state
  const [allSessions, setAllSessions] = useState<StoryTimeSessionHistoryItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const historyPageRef = useRef(1);
  const loadingMoreRef = useRef(false);

  const {
    data: sessionsData,
    isLoading: sessionsLoading,
    isError: sessionsError,
    refetch: refetchSessions,
  } = useStoryTimeSessions(1, SESSION_LIMIT);

  // Populate initial sessions list when first page loads
  useEffect(() => {
    if (sessionsData && historyPageRef.current === 1) {
      setAllSessions(sessionsData.sessions);
      setHasMore(sessionsData.has_more);
    }
  }, [sessionsData]);

  const {
    control,
    handleSubmit,
    setError,
    setValue,
    watch,
    formState: { errors },
  } = useForm<StoryTimePreferences>({
    defaultValues: { topic: '', lengthBand: 'medium', hints: false, targetLanguage: '' },
  });

  const selectedLanguage = watch('targetLanguage');
  const isJapanese = selectedLanguage.toLowerCase() === 'japanese';

  // Always enable furigana hints for Japanese
  useEffect(() => {
    setValue('hints', isJapanese);
  }, [isJapanese, setValue]);

  // Log when available languages change (debug aid — remove when no longer needed).
  useEffect(() => {
    console.debug('[StoryTime] availableLanguages loaded:', availableLanguages);
  }, [availableLanguages]);

  const onSubmit = async (data: StoryTimePreferences) => {
    setStoryResult(null);

    try {
      const response = await generateStory.mutateAsync({
        topic: data.topic,
        length_band: data.lengthBand,
        hints: data.hints,
        target_language: data.targetLanguage,
      });
      setActiveSessionId(response.session_id);
      setStoryResult({ response, topic: data.topic, storyLanguage: data.targetLanguage });
      historyPageRef.current = 1;
      setAllSessions([]); 
      await refetchSessions();
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        const body =
          typeof err.response?.data === 'object' && err.response?.data !== null
            ? (err.response.data as { status?: string; field?: string; message?: string })
            : {};

        // Truncation: the model ran out of output budget before finishing the
        // story (backend status "story_too_long", HTTP 502). Surface it as an
        // inline error on the length control so the user can pick a shorter
        // band, and stop it from falling through to a generic toast.
        if (body.status === 'story_too_long') {
          setError('lengthBand', { message: t('storyTimeTooLong') });
          return;
        }

        if (status === 422) {
          const fieldMap: Record<string, keyof StoryTimePreferences> = {
            topic: 'topic',
            length_band: 'lengthBand',
            hints: 'hints',
            target_language: 'targetLanguage',
          };
          const rhfField = body.field ? fieldMap[body.field] : undefined;

          if (rhfField && body.message) {
            setError(rhfField, { message: body.message });
          } else {
            setError('topic', { message: body.message ?? t('storyTimeValidationError') });
          }
          return;
        }
      }
      // All other errors: API client interceptor shows toast automatically
    }
  };

  const isPending = generateStory.isPending;

  const handleReset = () => {
    setStoryResult(null);
    setActiveSessionId(null);
    formRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleHistorySelect = (item: StoryTimeSessionHistoryItem) => {
    const storyHtml = item.story_html ?? item.story_preview;
    if (!storyHtml.trim()) return;
    const plainText = storyHtml.replace(/<[^>]*>/g, ' ').trim();
    const fallbackWordCount = plainText.length > 0 ? plainText.split(/\s+/).length : 0;

    console.debug('[StoryTime] handleHistorySelect — item.target_language:', item.target_language);
    console.debug('[StoryTime] availableLanguages at select time:', availableLanguages);
    if (item.target_language) {
      const inList = availableLanguages.includes(item.target_language);
      console.debug('[StoryTime] language in availableLanguages?', inList, '— setting anyway');
      setValue('targetLanguage', item.target_language);
    } else {
      console.debug('[StoryTime] No target_language on history item — dropdown unchanged.');
    }

    setActiveSessionId(item.id);
    setStoryResult({
      response: {
        session_id: item.id,
        story_html: storyHtml,
        difficulty_band: item.difficulty_band,
        word_count: item.word_count ?? fallbackWordCount,
        used_words: [],
      },
      topic: item.topic,
      storyLanguage: item.target_language,
    });
  };

  const handleDelete = async (sessionId: string) => {
    await deleteSession.mutateAsync(sessionId);
    setAllSessions((prev) => prev.filter((s) => s.id !== sessionId));
    if (activeSessionId === sessionId) {
      setStoryResult(null);
      setActiveSessionId(null);
    }
  };

  const handleLoadMore = async () => {
    if (loadingMoreRef.current || !hasMore) {
      return;
    }

    loadingMoreRef.current = true;
    const nextPage = historyPageRef.current + 1;
    setLoadingMore(true);
    try {
      const data = await queryClient.fetchQuery({
        queryKey: storyTimeKeys.sessions(nextPage, SESSION_LIMIT),
        queryFn: () => getStorySessions({ page: nextPage, limit: SESSION_LIMIT }),
        staleTime: 30_000,
      });
      setAllSessions((prev) => {
        const existingIds = new Set(prev.map((s) => s.id));
        const newItems = data.sessions.filter((s) => !existingIds.has(s.id));
        return [...prev, ...newItems];
      });
      setHasMore(data.has_more);
      historyPageRef.current = nextPage;
    } catch {
      // API errors are surfaced by the axios interceptor
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="h4" component="h1" sx={{ flex: 1 }}>
          {t('storyTime')}
        </Typography>
        <Tooltip title={t('showTour', 'Show Tour')}>
          <IconButton onClick={resetTour} color="primary">
            <Badge variant="dot" color="secondary" invisible={!wasSkipped}>
              <HelpOutlineIcon />
            </Badge>
          </IconButton>
        </Tooltip>
      </Box>

      <Paper ref={formRef} sx={{ p: 4 }}>
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="topic"
            control={control}
            rules={{
              required: t('storyTimeTopicRequired'),
              maxLength: { value: 200, message: t('storyTimeTopicMaxLength') },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label={t('storyTimeTopicLabel')}
                margin="normal"
                error={!!errors.topic}
                helperText={errors.topic?.message}
                disabled={isPending}
                data-tour="storytime-topic"
              />
            )}
          />

          <Controller
            name="lengthBand"
            control={control}
            rules={{ required: t('storyTimeLengthRequired') }}
            render={({ field }) => (
              <FormControl
                fullWidth
                margin="normal"
                error={!!errors.lengthBand}
                disabled={isPending}
                data-tour="storytime-length"
              >
                <InputLabel id="length-band-label">{t('storyTimeLengthLabel')}</InputLabel>
                <Select {...field} labelId="length-band-label" label={t('storyTimeLengthLabel')}>
                  {STORYTIME_LENGTH_BANDS.map((band) => (
                    <MenuItem key={band.value} value={band.value}>
                      {t(band.labelKey)}
                    </MenuItem>
                  ))}
                </Select>
                {errors.lengthBand && (
                  <FormHelperText>
                    {errors.lengthBand.message}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          />

          {availableLanguages.length > 1 && (
            <Controller
              name="targetLanguage"
              control={control}
              rules={{ required: t('storyTimeLanguageRequired') }}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  margin="normal"
                  error={!!errors.targetLanguage}
                  disabled={isPending}
                  data-tour="storytime-language"
                >
                  <InputLabel id="target-language-label">{t('storyTimeLanguageLabel')}</InputLabel>
                  <Select {...field} labelId="target-language-label" label={t('storyTimeLanguageLabel')}>
                    {availableLanguages.map((lang) => (
                      <MenuItem key={lang} value={lang}>
                        {lang}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.targetLanguage && (
                    <FormHelperText>{errors.targetLanguage.message}</FormHelperText>
                  )}
                </FormControl>
              )}
            />
          )}

          <Box sx={{ mt: 3 }}>
            <Button
              variant="contained"
              type="submit"
              disabled={isPending}
              fullWidth
              startIcon={isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
              data-tour="storytime-generate"
            >
              {t('generateStory')}
            </Button>
          </Box>
        </Box>
      </Paper>

      {storyResult && (
        <StoryReader
          result={storyResult.response}
          topic={storyResult.topic}
          onReset={handleReset}
          storyLanguage={storyResult.storyLanguage}
          sessionId={activeSessionId ?? undefined}
          onDelete={activeSessionId ? () => handleDelete(activeSessionId) : undefined}
        />
      )}

      {/* History panel */}
      <Box sx={{ mt: 4 }}>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="h6" component="h2" gutterBottom>
          {t('storyHistory')}
        </Typography>
        <StoryHistoryList
          sessions={allSessions}
          loading={sessionsLoading}
          error={sessionsError}
          hasMore={hasMore}
          loadingMore={loadingMore}
          onSelect={handleHistorySelect}
          onRetry={() => refetchSessions()}
          onLoadMore={handleLoadMore}
          onDelete={handleDelete}
        />
      </Box>

      {/* First-visit StoryTime tour */}
      <StoryTimeTour open={showTour} onComplete={completeTour} onSkip={skipTour} />
    </Container>
  );
};
